import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AD_CONFIG from '../config/adConfig';

/**
 * Google AdSense Ad Component
 * 
 * Displays Google AdSense ads with support for different formats and sizes.
 * Automatically hides ads for paid/premium members (ad-free benefit).
 * 
 * Usage:
 *   <AdSense slot="landingSidebar" format="auto" />
 *   <AdSense slot="blogSidebar" format="rectangle" />
 * 
 * Props:
 *   - slot: Slot key from adConfig.js OR direct slot ID (required)
 *   - format: 'auto' | 'rectangle' | 'horizontal' | 'vertical' | 'fluid'
 *   - style: Custom styles for the ad container
 *   - className: Additional CSS classes
 */

const AD_CLIENT = AD_CONFIG.publisherId;

// Predefined sizes for common formats
const FORMAT_STYLES = {
  auto: { display: 'block' },
  rectangle: { display: 'inline-block', width: 300, height: 250 },
  horizontal: { display: 'block', width: '100%', height: 90 },
  vertical: { display: 'inline-block', width: 160, height: 600 },
  fluid: { display: 'block', width: '100%' },
  'in-article': { display: 'block', textAlign: 'center' },
  'in-feed': { display: 'block' },
};

// Test mode placeholder component
function AdPlaceholder({ slot, format, style, className }) {
  const testStyle = {
    ...FORMAT_STYLES[format],
    ...style,
    backgroundColor: '#f0f0f0',
    border: '2px dashed #ccc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666',
    fontSize: '12px',
    minHeight: format === 'auto' ? 100 : undefined,
  };
  
  return (
    <div 
      className={`adsense-placeholder ${className}`}
      style={testStyle}
      data-testid="adsense-placeholder"
    >
      <span>Ad: {slot} ({format})</span>
    </div>
  );
}

export default function AdSense({ 
  slot, 
  format = 'auto', 
  style = {}, 
  className = '',
  fallback = null 
}) {
  const adRef = useRef(null);
  const { user } = useAuth();
  const location = useLocation();
  const isAdFree = user?.membership_tier === 'paid' || user?.membership_tier === 'premium';
  
  // Resolve slot ID from config or use direct ID
  const slotId = AD_CONFIG.slots[slot] || slot || '';
  const testMode = AD_CONFIG.testMode;
  const shouldShow = AD_CONFIG.shouldShowAds(location.pathname);

  useEffect(() => {
    if (isAdFree || testMode || !slotId || !shouldShow) return;

    try {
      if (window.adsbygoogle && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, [slotId, isAdFree, testMode, shouldShow]);

  // Early returns for non-display cases
  if (isAdFree || !shouldShow) return null;
  if (testMode) return <AdPlaceholder slot={slot} format={format} style={style} className={className} />;
  if (!slotId) return fallback;

  const adStyle = { ...FORMAT_STYLES[format], ...style };
  const isInFeed = format === 'in-feed';
  const dataAdFormat = format === 'auto' ? 'auto' : isInFeed ? 'fluid' : undefined;

  return (
    <div className={`adsense-container ${className}`} data-testid="adsense-container">
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={adStyle}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slotId}
        data-ad-format={dataAdFormat}
        data-ad-layout-key={isInFeed ? AD_CONFIG.inFeedLayoutKey : undefined}
        data-full-width-responsive={format === 'auto' ? 'true' : undefined}
      />
    </div>
  );
}

/**
 * Ad wrapper with label for transparency + subtle upgrade upsell
 * for members on the free (Founding Member) tier.
 * Renders nothing for Charter/Premium members so we don't show
 * "Charter Members see no ads" to Charter Members themselves.
 */
export function AdWithLabel({ children, label = 'Advertisement', className = '' }) {
  const { user } = useAuth();
  const isAdFree = user?.membership_tier === 'paid' || user?.membership_tier === 'premium';
  if (isAdFree) return null;

  return (
    <div className={`ad-wrapper ${className}`} data-testid="ad-wrapper">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 text-center">
        {label}
      </div>
      {children}
      <div className="mt-1 text-center">
        <a
          href="/membership"
          className="text-[10px] text-muted-foreground hover:text-primary underline underline-offset-2"
          data-testid="ad-charter-upsell"
        >
          Charter Members see no ads →
        </a>
      </div>
    </div>
  );
}

/**
 * Sidebar ad component - 300x250 rectangle
 */
export function SidebarAd({ slot, className = '' }) {
  return (
    <AdWithLabel className={className}>
      <AdSense slot={slot} format="rectangle" style={{ width: 300, height: 250 }} />
    </AdWithLabel>
  );
}

/**
 * In-feed ad component - appears between feed items
 */
export function InFeedAd({ slot, className = '' }) {
  return (
    <div className={`my-4 ${className}`}>
      <AdWithLabel label="Sponsored">
        <AdSense slot={slot} format="in-feed" />
      </AdWithLabel>
    </div>
  );
}

/**
 * Banner ad component - full width horizontal
 */
export function BannerAd({ slot, className = '' }) {
  return (
    <div className={`w-full ${className}`}>
      <AdWithLabel>
        <AdSense slot={slot} format="horizontal" />
      </AdWithLabel>
    </div>
  );
}

/**
 * In-article ad component - appears within article content
 */
export function InArticleAd({ slot, className = '' }) {
  return (
    <div className={`my-6 ${className}`}>
      <AdSense slot={slot} format="in-article" />
    </div>
  );
}
